import _extractStyleAttributes from "../quill/extractStyleAttributes.js";
const extractStyleAttributes = _extractStyleAttributes;

/**
 * Invisible monospace cell matching one teletext spacing attribute.
 * Letter-spacing in ClosedCaptionPreview applies to these like normal characters.
 */
const SPACER = '<span class="ttx-control-space" aria-hidden="true">&nbsp;</span>';

/**
 * Count teletext spacing attribute cells for a color/bg span open.
 * Matches ebuStl.js encodeText appendColorControls (teletext path):
 * - background only → color + 1d + default fg = 3
 * - color only → 1
 * - background + color → color + 1d + fg color = 3
 */
function countTeletextColorSpacing(spanInfo) {
  if (!spanInfo || !spanInfo["background-color"] && !spanInfo.color) {
    return 0;
  }
  let count = 0;
  if (spanInfo["background-color"]) {
    count += 2; // broadcast color code + New Background (0x1d)
    if (!spanInfo.color) {
      count += 1; // default foreground (00 or 07)
    }
  }
  if (spanInfo.color) {
    count += 1;
  }
  return count;
}
function makeSpacers(n) {
  if (n <= 0) {
    return "";
  }
  return SPACER.repeat(n);
}

/**
 * Insert invisible spacers for teletext color/background control codes so
 * Closed Caption Preview matches EBU-STL teletext column consumption.
 *
 * Spacers are only inserted for the leading color/bg span at the start of a
 * line (start of HTML or immediately after a <p> open). On air, the leading
 * control codes are what push a left-aligned row right; mid-line color
 * changes overwrite existing cells within the row, so simulating them in the
 * preview would add gaps that don't exist in broadcast.
 *
 * Does not emulate start-box, double-height, resets, or
 * consumeAdjacentSpaceForColor.
 *
 * @param {string} html Caption HTML (Quill), e.g. <p><span style="...">...</span></p>
 * @returns {string} HTML with ttx-control-space spacers inserted
 */
function applyTeletextControlSpacing(html) {
  if (!html) {
    return html;
  }
  let out = "";
  let atLineStart = true;
  const characters = html.split("");
  const peek = n => characters.slice(0, n).join("");

  // Insert leading spacers for a color/bg open tag; no-op mid-line.
  const applyOpenControls = spanInfo => {
    if (!atLineStart) {
      return;
    }
    const n = countTeletextColorSpacing(spanInfo);
    if (n <= 0) {
      return;
    }
    out += makeSpacers(n);
    atLineStart = false;
  };

  // Consume a full tag body (after '<'), returning it or null when malformed.
  const readTagBody = () => {
    const closeIdx = characters.findIndex(c => c === ">");
    if (closeIdx === -1) {
      return null;
    }
    return characters.splice(0, closeIdx + 1).join("");
  };
  const handleStyledOpen = () => {
    const tagBody = readTagBody();
    if (tagBody === null) {
      out += "<";
      return;
    }
    const spanInfo = extractStyleAttributes("<" + tagBody);
    applyOpenControls(spanInfo);
    out += "<" + tagBody;
  };
  while (characters.length > 0) {
    const char = characters.shift();
    if (char !== "<") {
      out += char;
      atLineStart = false;
      continue;
    }
    if (peek(6) === "/span>") {
      characters.splice(0, 6);
      out += "</span>";
    } else if (peek(8) === "/strong>") {
      characters.splice(0, 8);
      out += "</strong>";
    } else if (peek(4) === "/em>") {
      characters.splice(0, 4);
      out += "</em>";
    } else if (peek(3) === "/u>") {
      characters.splice(0, 3);
      out += "</u>";
    } else if (peek(4) === "span") {
      handleStyledOpen();
    } else if (peek(6) === "strong") {
      handleStyledOpen();
    } else if (peek(2) === "em" && (characters[2] === ">" || characters[2] === " " || characters[2] === "/")) {
      handleStyledOpen();
    } else if (peek(1) === "u" && (characters[1] === ">" || characters[1] === " " || characters[1] === "/")) {
      handleStyledOpen();
    } else {
      // Pass through any other tag unchanged (p, br, etc.)
      const tagBody = readTagBody();
      if (tagBody === null) {
        out += "<";
      } else {
        out += "<" + tagBody;
        // A new paragraph starts a new teletext row
        if (/^p(\s|>)/i.test(tagBody)) {
          atLineStart = true;
        }
      }
    }
  }
  return out;
}
applyTeletextControlSpacing.countTeletextColorSpacing = countTeletextColorSpacing;
export default applyTeletextControlSpacing;